import React, { useEffect, useState } from "react";
import ScrollToTopOnRouteChange from "components/ScrollToTopOnRouteChange/ScrollToTopOnRouteChange";
import userResources from "api/userResources";
import { selectUserInfo } from "../../../redux/userSlice";
import { useSelector } from "react-redux";
import { completeLogo } from "utils/images";
import { useForm } from "react-hook-form";
import InputField from "components/fields/InputField";
import SubmitButton from "components/SubmitButton/SubmitButton";
import { toast } from "react-toastify";
import { AiFillPrinter } from "react-icons/ai";
import { FaEdit } from "react-icons/fa";
import ConfirmModal from "components/ConfirmModal/ConfirmModal";
import { useNavigate } from "react-router-dom";
import { creditCardNotFound } from "utils/images";
import { IoReloadOutline } from "react-icons/io5";
export default function CreditCardsInfo() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate();
  const userInfo = useSelector(selectUserInfo);
  const [creditCardsData, setCreditCardsData] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 1, // Show 2 records per page
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [params, setParams] = useState();

  const fetchCreditCards = async (pageIndex, pageSize, params) => {
    setIsLoading(true);
    let toastId;

    // Check if any key in params has a truthy value
    const shouldShowToast =
      params && Object.values(params).some((val) => !!val);

    try {
      if (shouldShowToast) {
        toastId = toast.loading("Fetching data...");
      }
      const response = await userResources.creditCards(
        pageIndex,
        pageSize,
        params
      );
      setCreditCardsData(response.data.data);
      setTotalRows(response.data.paginate_data.total);

      if (shouldShowToast) {
        toast.update(toastId, {
          render: "Data fetched successfully",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error fetching Credit Cards:", error);
      if (shouldShowToast) {
        toast.update(toastId, {
          render: "Error fetching Credit Cards",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCreditCards(pagination.pageIndex, pagination.pageSize, params);
  }, [pagination]);

  const handleNextPage = () => {
    if (pagination.pageIndex < Math.ceil(totalRows / pagination.pageSize) - 1) {
      setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
    }
  };

  const handlePreviousPage = () => {
    if (pagination.pageIndex > 0) {
      setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }));
    }
  };

  const printDocument = (id) => {
    const printContent = document.getElementById(`document-${id}`);
    if (printContent) {
      const printWindow = window.open(
        "",
        "_blank",
        "width=800,height=600,scrollbars=yes,resizable=no"
      );

      printWindow.document.write(`
        <html>
          <head>
          <title>.</title>
            <style>
              /* Ensure the logo has a proper size in the print view */
              img {
                max-width: 200px;
                height: auto;
                display: block;
                margin: 0 auto;
              }
              body {
                // font-family: Arial, sans-serif;
                margin: 20px;
                padding: 0;
              }
              .text-center {
                text-align: center;
              }
              span {
                text-decoration: underline; /* Underline every span */
                color: gray;
              }
              footer {
                display: none
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
        </html>
      `);

      printWindow.document.close();

      // Trigger print after ensuring the content is fully loaded
      printWindow.onload = () => {
        printWindow.focus(); // Focus the new tab before printing
        printWindow.print();
      };
    }
  };

  const onSubmit = (data) => {
    // Remove keys with empty values from data
    const cleanedData = Object.fromEntries(
      Object.entries(data).filter(([key, value]) => value !== "")
    );

    // Check if at least one field is filled
    const hasAtLeastOneValue = Object.values(cleanedData).some(
      (value) => value
    );
    if (!hasAtLeastOneValue) {
      toast.warn("Please fill in at least one field to search!");
      return;
    }

    setParams(cleanedData);
  };

  const handleDelete = async (id) => {
    // Show loading toast
    const loadingToastId = toast.loading(
      "Deleting credit card authorization form..."
    );

    try {
      await userResources.creditCardDelete(id); // Attempt to delete the airline

      // Update toast to show success
      toast.update(loadingToastId, {
        render: "credit card authorization deleted successfully.",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });

      fetchCreditCards(pagination.pageIndex, pagination.pageSize, params);
      setDisabled(false);
    } catch (error) {
      console.error("Error deleting credit card authorization:", error);

      // Update toast to show error
      const errorMessage =
        error.response?.data?.message ||
        "Failed to delete credit card authorization. Please try again.";
      toast.update(loadingToastId, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      setDisabled(false);
    } finally {
      // Dismiss the loading toast if not already dismissed
      if (toast.isActive(loadingToastId)) {
        toast.dismiss(loadingToastId);
      }
      setDisabled(false);
    }
  };

  useEffect(() => {
    fetchCreditCards(pagination.pageIndex, pagination.pageSize, params);
  }, [params]);

  const renderDocument = (data) => {
    return (
      <div className="rounded bg-white p-4">
        <div className="flex items-center justify-between">
          {/* Print Button */}

          <AiFillPrinter
            onClick={() => printDocument(data.id)}
            className="cursor-pointer text-gray-900"
            size={25}
          />
          <div className="flex gap-2">
            <FaEdit
              className="cursor-pointer text-gray-900"
              size={25}
              onClick={() =>
                navigate("/admin/credit-card-authorization", { state: data })
              }
            />
            <ConfirmModal
              onConfirm={handleDelete}
              title="Delete"
              message="Are you sure you want to delete?"
              employeeId={data.id}
            />
          </div>
        </div>
        <div
          key={data.id}
          id={`document-${data.id}`}
          className="font-semibold text-black"
        >
          {/* Header Section */}
          <div className="text-center">
            <img
              src={completeLogo}
              alt="Logo"
              style={{
                maxWidth: "150px",
                height: "auto",
                margin: "0 auto",
              }}
            />
            <h5>CONFIDENTIAL: CREDIT CARD AUTHORIZATION FORM</h5>
          </div>
          {/* Date Section */}
          <p>
            DATE:{" "}
            <span className="font-light text-blueSecondary underline">
              {new Date(data.created_at).toLocaleDateString()}
            </span>
          </p>

          {/* Authorization Section */}
          <p>
            IN LIEU OF MY CREDIT CARD IMPRINT, I (CREDIT CARD HOLDER'S NAME):
          </p>
          <span className="block font-light text-blueSecondary underline">
            {data.holder_name}
          </span>

          <h5>HEREBY AUTHORIZE: KATARIA TOURS & TRAVEL</h5>
          <p>
            TO CHARGE MY (CREDIT CARD / MASTERCARD / AMEX NO.) (EXP DATE) (3/4
            DIGIT)
          </p>

          {/* Credit Card Information */}
          <p>
            CREDIT CARD TYPE:{" "}
            <span className="font-light text-blueSecondary underline">
              {data.type}
            </span>
          </p>
          <p className="flex gap-1">
            <span className="font-light text-blueSecondary underline">
              {data.card_number}
            </span>{" "}
            EXP{" "}
            <span className="font-light text-blueSecondary underline">
              {data.expire_date}
            </span>{" "}
            CVV{" "}
            <span className="font-light text-blueSecondary underline">
              {data.cvv}
            </span>
          </p>

          {/* Payment Details */}
          <p>
            IN THE CAD DOLLARS AMOUNT OF:{" "}
            <span className="font-light text-blueSecondary underline">
              ${data.amount}
            </span>
          </p>

          <p className="flex gap-1">
            AIRLINE:{" "}
            <span className="font-light text-blueSecondary underline">
              {data.airLine?.airline_name}
            </span>{" "}
            TRAVEL DATE:{" "}
            <span className="font-light text-blueSecondary underline">
              {data.travel_date}
            </span>
          </p>

          {/* Transportation Section */}
          <p>
            FOR PAYMENT OF TRANSPORTATION FOR MYSELF AND/OR FULL NAME OF
            PASSENGER IF OTHER THAN THE CARD HOLDER:
          </p>
          <span className="block font-light text-blueSecondary underline">
            {data.transportation}
          </span>

          {/* Billing Address */}
          <p>CREDIT CARD BILLING ADDRESS FOR CREDIT CARD VERIFICATION:</p>
          <div className="flex gap-1">
            <span className="font-light text-blueSecondary underline">
              {data.country}
            </span>
            <p className="flex gap-1">
              CITY:{" "}
              <span className="font-light text-blueSecondary underline">
                {data.city}
              </span>{" "}
              STATE:{" "}
              <span className="font-light text-blueSecondary underline">
                {data.state}
              </span>
            </p>
          </div>
          <p>
            POSTAL CODE:{" "}
            <span className="font-light text-blueSecondary underline">
              {data.postal_code}
            </span>
          </p>
          <p>
            PHONE NO:{" "}
            <span className="font-light text-blueSecondary underline">
              {data.phone_number}
            </span>
          </p>

          {/* Acknowledgment */}
          <p>
            BY SIGNING BELOW, I ACKNOWLEDGE THE CHARGES DESCRIBED HEREON.
            PAYMENT IN FULL WILL BE MADE WHEN BILLED OR IN ACCORDANCE WITH THE
            STANDARD POLICY OF MY CREDIT CARD COMPANY.
          </p>

          {/* Signature Section */}
          <div className="grid grid-cols-2 gap-5">
            <p>SIGNATURES OF CARD HOLDER:</p>
            <img src={data.full_path} alt="Signature" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="mt-6">
      <ScrollToTopOnRouteChange />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 rounded p-4 shadow-lg dark:bg-gray-900"
        >
          <div className="flex items-center justify-between ">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-[#bb8e25]">
              Search Credit Card
            </h2>
            <IoReloadOutline
              onClick={() => {
                setParams({}); // Clear params or perform any other action
                reset();
              }}
              className={`cursor-pointer text-gray-900 dark:text-white ${
                isLoading ? "animate-spin" : ""
              }`}
              size={25}
            />
          </div>

          {/* Full Name Field */}
          <InputField
            label="Full Name"
            id="holder_name"
            name="holder_name"
            placeholder="Enter full name"
            register={register}
            rules={{}}
            errorMessage={errors.holder_name?.message}
          />

          {/* Credit Card Type */}
          <div>
            <label htmlFor="" className="text-gray-900 dark:text-[#bb8e25]">
              Credit Card Type
            </label>
            <select
              id="type"
              name="type"
              {...register("type")} // This connects the field to React Hook Form
              className={`mt-2 w-full rounded-md border  py-3 ${
                errors.type ? "border-red-500" : "border-gray-400"
              } bg-transparent outline-none dark:text-white`}
            >
              <option value="" className="text-gray-400">
                Select Card Type
              </option>
              <option value="MasterCard">MasterCard</option>
              <option value="American Express">American Express</option>
              <option value="Visa">Visa</option>
            </select>
            {errors.type && (
              <p className="mt-1 text-sm text-red-500">{errors.type.message}</p>
            )}
          </div>

          {/* Card Number */}
          <InputField
            label="Card Number"
            id="card_number"
            type="number"
            name="card_number"
            placeholder="Enter card number"
            register={register}
            // rules={{
            //   pattern: {
            //     value: /^\d{16}$/,
            //     message: "Card Number must be 16 digits",
            //   },
            // }}
            errorMessage={errors.card_number?.message}
          />

          {/* Expiration Date */}
          <InputField
            label="Expiration Date"
            id="expire_date"
            name="expire_date"
            placeholder="MM/YY"
            register={register}
            rules={{
              pattern: {
                value: /^(0[1-9]|1[0-2])\/\d{2}$/,
                message: "Invalid expiration date format (MM/YY)",
              },
            }}
            errorMessage={errors.expire_date?.message}
          />

          {/* CVV */}
          <InputField
            label="CVV"
            type="number"
            id="cvv"
            name="cvv"
            placeholder="Enter CVV"
            register={register}
            // rules={{
            //   pattern: {
            //     value: /^\d{3,4}$/,
            //     message: "CVV must be 3 or 4 digits",
            //   },
            // }}
            errorMessage={errors.cvv?.message}
          />

          {/* Phone Number */}
          <InputField
            label="Phone Number"
            type="number"
            id="phone_number"
            name="phone_number"
            placeholder="Enter phone number"
            register={register}
            errorMessage={errors.phone_number?.message}
          />

          {/* Submit Button */}
          <SubmitButton add="Search" extraClass="btn w-full border-0" />
        </form>
        <div className="md:col-span-2">
          {/* {isLoading ? (
            <p>Loading...</p>
          ) : ( */}
          {creditCardsData && creditCardsData.length > 0 ? (
            <div>
              {creditCardsData.map((data, index) => (
                <div key={index}>{renderDocument(data)}</div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <img
                src={creditCardNotFound}
                alt="No credit cards found"
                className="h-auto max-w-full"
              />
            </div>
          )}

          {/* )} */}
        </div>
      </div>

      <div className="pagination-controls mt-4 flex justify-end">
        <button
          onClick={handlePreviousPage}
          disabled={pagination.pageIndex === 0}
          className="mr-2 rounded bg-gray-300 px-4 py-2 text-gray-800 disabled:opacity-50"
        >
          Previous
        </button>
        <button
          onClick={handleNextPage}
          disabled={
            pagination.pageIndex >=
            Math.ceil(totalRows / pagination.pageSize) - 1
          }
          className="rounded bg-[#bb8e25] px-4 py-2 text-white hover:bg-[#8f7029] disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </section>
  );
}
